import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import { GardenFlower, TimeOfDay, WeatherType } from '../types';
import { getTulipStyle } from '../utils/tulipColorUtils';
import { HydrationState } from '../utils/wateringUtils';

interface FlowerGraphicProps {
  flower?: GardenFlower;
  index: number;
  timeOfDay?: TimeOfDay;
  weather?: WeatherType;
  isSelected?: boolean;
  colorValue?: string;
  hydrationState?: HydrationState;
  isBeingWatered?: boolean;
  onClick: () => void;
}

export const FlowerGraphic: React.FC<FlowerGraphicProps> = React.memo(({
  flower,
  index,
  timeOfDay = 'day',
  weather = 'sunny',
  isSelected = false,
  colorValue,
  hydrationState,
  isBeingWatered = false,
  onClick,
}) => {
  const isSprout = flower?.growthStage === 'sprout';
  const isBud = flower?.growthStage === 'bud';
  const flowerType = flower?.flowerType || 'golden-tulip';

  // Map flower color palette
  const resolvedColor = useMemo(() => {
    if (colorValue) {
      return getTulipStyle(colorValue);
    }
    switch (flowerType) {
      case 'pink-rose':
        return { className: 'color-pink', style: {} };
      case 'white-lily':
        return { className: 'color-white', style: {} };
      case 'blue-hydrangea':
        return { className: 'color-lavender', style: {} };
      case 'sunflower':
        return { className: 'color-peach', style: {} };
      case 'golden-tulip':
      default:
        const defClass = (index % 4 === 1) ? 'color-peach' : (index % 4 === 2) ? 'color-pink' : (index % 4 === 3) ? 'color-ruby' : 'color-golden';
        return { className: defClass, style: {} };
    }
  }, [colorValue, flowerType, index]);

  // Natural staggered sizing & angles matching para-ti-mvd
  const baseScale = [0.92, 1.04, 0.96, 0.88][index % 4];
  const rotAngle = [-5.5, -1.5, 3.5, 6.0][index % 4];
  const windDuration = (6.4 + (index % 4) * 0.9).toFixed(1) + 's';
  const windAmp = weather === 'rainy' ? '7.5deg' : '4.8deg';

  // Realistic Withering Calculations (Gradual wilting over 3 days)
  const witherLevel = hydrationState?.witherLevel ?? 0;
  const stemDroopDeg = hydrationState?.stemDroopDeg ?? 0;
  const headDroopDeg = hydrationState?.headDroopDeg ?? 0;
  const leafDroopDeg = hydrationState?.leafDroopDeg ?? 0;

  // Botanical physics variations per flower index for natural realism
  const stemDirection = index % 2 === 0 ? -1 : 1;
  const stemBow = stemDirection * stemDroopDeg * (0.85 + (index % 3) * 0.15);
  const headTilt = headDroopDeg * (0.9 + (index % 2) * 0.25);
  const headRotate = stemDirection * headTilt;
  const headDroopY = witherLevel * 0.14;

  const witherFilter = hydrationState
    ? `saturate(${hydrationState.saturation}) sepia(${hydrationState.sepia}) brightness(${hydrationState.brightness})`
    : 'none';
  const glowOpacity = hydrationState ? Math.max(0, (hydrationState.hydrationPercent - 25) / 75) : 1;
  const showDewdrops = isBeingWatered || (hydrationState && hydrationState.hydrationPercent >= 75);

  // Base grass blades (mata) for deep natural rooting
  const mataBlades = useMemo(() => {
    return Array.from({ length: 9 }).map((_, k) => {
      const px = 10 + (k / 8) * 80 + (Math.sin(k * 2.7) * 3);
      const alto = 8 + ((k * 4) % 8);
      const inclina = (px - 50) * 0.34 + Math.sin(k * 1.8) * 6;
      const width = 1.4 + (k % 3) * 0.4;
      const hue = 88 + (k * 5) % 24;
      const dur = (2.6 + (k % 4) * 0.6).toFixed(2) + 's';
      const delay = (-(k * 0.35) % 2.5).toFixed(2) + 's';
      return { id: k, px, alto, inclina, width, hue, dur, delay };
    });
  }, []);

  // Stem height & growth interpolation (Scaled down to elegant, realistic proportions)
  const stemHeight = isSprout ? 25 : isBud ? 65 : [105, 138, 110, 92][index % 4];
  const headScale = isSprout ? 0.35 : isBud ? 0.65 : 1;
  const leafScale = isSprout ? 0.45 : 1;

  return (
    <motion.div
      className="relative flex flex-col items-center cursor-pointer select-none group"
      onClick={onClick}
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.96 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      id={`flower-${flower?.id || index}`}
    >
      {/* Selection Halo Indicator */}
      {isSelected && (
        <div className="absolute -top-2 w-16 h-16 rounded-full bg-amber-400/20 blur-lg pointer-events-none animate-pulse" />
      )}

      {/* The Master Tulip Component structure with Gradual Withering */}
      <div
        className={`mvd-flower ${resolvedColor.className}`}
        style={
          {
            ...resolvedColor.style,
            '--scale': baseScale,
            '--rot': `${rotAngle}deg`,
            '--wind': windDuration,
            '--amp': windAmp,
            filter: witherFilter,
            height: `${stemHeight + 56}px`,
          } as React.CSSProperties
        }
      >
        {/* Sombra de contacto en la base */}
        <span className="sombra" />

        {/* Bulbo base bajo la superficie con raicillas */}
        <span className="bulbo" />

        {/* Cuello pálido que conecta bulbo con tallo */}
        <span className="cuello" />

        {/* Hojas auténticas curvadas de tulipán (con marchitamiento realista por pérdida de turgencia) */}
        <span
          className="hoja h1"
          style={{
            transform: `translateX(-50%) rotate(${-17 - leafDroopDeg * 0.85}deg) translateY(${leafDroopDeg * 0.2}px) scale(${leafScale})`,
          }}
        />
        <span
          className="hoja h2"
          style={{
            transform: `translateX(-50%) rotate(${-2 + stemDirection * leafDroopDeg * 0.4}deg) translateY(${leafDroopDeg * 0.15}px) scale(${leafScale})`,
          }}
        />
        <span
          className="hoja h3"
          style={{
            transform: `translateX(-50%) rotate(${15 + leafDroopDeg * 0.85}deg) translateY(${leafDroopDeg * 0.2}px) scale(${leafScale})`,
          }}
        />

        {/* Tallo articulado de 3 segmentos con flexión biomecánica y curvatura por sequía */}
        <div
          className="stem"
          style={{
            height: `${stemHeight}px`,
            transform: `translateX(-50%) rotate(${stemBow}deg)`,
          }}
        >
          <div className="seg s1">
            <span className="bar" />
            <div className="seg s2">
              <span className="bar" />
              <div
                className="seg s3"
                style={{
                  transform: `rotate(${stemBow * 0.4}deg)`,
                }}
              >
                <span className="bar" />

                {/* Cabeza de la flor (cáliz, copa, pétalos, rocío y marchitamiento) */}
                <div
                  className="head"
                  style={{
                    transform: `translateX(-50%) translateY(${headDroopY}px) scale(${headScale}) rotate(${headRotate}deg)`,
                  }}
                >
                  <div
                    className={`head-wind ${isBeingWatered ? 'head-water-bounce' : ''}`}
                    style={
                      isBeingWatered
                        ? {
                            animationName: 'headWaterBounce',
                            animationDuration: '0.7s',
                            animationTimingFunction: 'ease-in-out',
                            animationIterationCount: 'infinite',
                            willChange: 'transform',
                          }
                        : undefined
                    }
                  >
                    {/* Resplandor cálido botánico (se apaga gradualmente si se marchita) */}
                    <span
                      className="bloom-glow"
                      style={{ opacity: glowOpacity }}
                    />

                    <div className="petals">
                      {/* Copa central del tulipán */}
                      <span className="cup" />

                      {/* Lóbulos de pétalos exteriores e interiores */}
                      <span className="lobe l1" />
                      <span className="lobe l4" />
                      <span className="lobe l2" />
                      <span className="lobe l3" />

                      {/* Gotita / receptáculo base de unión con el pedúnculo */}
                      <span className="drop" />

                      {/* Salpicaduras activas en la flor durante el riego */}
                      {isBeingWatered && (
                        <div className="absolute inset-0 pointer-events-none z-20">
                          <span
                            className="absolute rounded-full bg-white/95 shadow-[0_0_6px_#38bdf8] animate-ping"
                            style={{ left: '42%', top: '22%', width: '4.5px', height: '4.5px' }}
                          />
                          <span
                            className="absolute rounded-full bg-sky-200/90 shadow-[0_0_5px_#7dd3fc] animate-ping"
                            style={{ left: '58%', top: '28%', width: '4px', height: '4px', animationDelay: '0.22s' }}
                          />
                        </div>
                      )}

                      {/* Rocío botánico cristalino cuando están hidratadas o tras ser regadas */}
                      {showDewdrops && (
                        <>
                          <span
                            className="mvd-dewdrop"
                            style={{
                              left: '30%',
                              top: '36%',
                              width: '4.5px',
                              height: '4.5px',
                              animationDelay: `${index * 0.4}s`,
                            }}
                          />
                          <span
                            className="mvd-dewdrop"
                            style={{
                              left: '65%',
                              top: '46%',
                              width: '3.6px',
                              height: '3.6px',
                              animationDelay: `${index * 0.4 + 0.9}s`,
                            }}
                          />
                          <span
                            className="mvd-dewdrop"
                            style={{
                              left: '48%',
                              top: '64%',
                              width: '3px',
                              height: '3px',
                              animationDelay: `${index * 0.4 + 1.8}s`,
                            }}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mata de césped que abraza la base del tallo para fijarlo al suelo */}
        <div className="mata">
          {mataBlades.map((b) => (
            <span
              key={b.id}
              style={
                {
                  left: `${b.px.toFixed(1)}%`,
                  width: `${b.width.toFixed(1)}px`,
                  height: `${b.alto}px`,
                  marginLeft: '-1px',
                  background: `linear-gradient(180deg, hsl(${b.hue} 52% 46%), hsl(${b.hue + 14} 46% 22%))`,
                  '--r0': `${(b.inclina - 7).toFixed(1)}deg`,
                  '--r1': `${(b.inclina + 7).toFixed(1)}deg`,
                  animationDuration: b.dur,
                  animationDelay: b.delay,
                  opacity: 0.85,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        {/* Ondas sutiles de agua al caer las gotas sobre la tierra durante el riego */}
        {isBeingWatered && (
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-12 h-6 pointer-events-none flex items-center justify-center">
            <span className="splash-ring absolute w-8 h-4 rounded-full border border-sky-300/60" />
            <span
              className="splash-ring absolute w-10 h-5 rounded-full border border-teal-300/40"
              style={{ animationDelay: '0.4s' }}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
});

FlowerGraphic.displayName = 'FlowerGraphic';

